import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { interval } from 'rxjs';
import { CanalOrigemContatoEnum } from '../../../shared/enum/canal-origem-contato.enum';
import { Contato } from '../../../shared/model/contato/contato.model';
import { CelularMaskPipe } from '../../../shared/pipe/celular-mask.pipe';
import { RestricaoService } from '../../../shared/service/restricao.service';
import { MethodsUtils } from '../../../shared/util/methods.utils';
import { animacaoExpandir, animacaoRevelar } from '../../utils/chat-animacoes.utils';
import { CHAVE_MOSTRAR_NUMERO } from './utils/conversa-cabecalho.constantes';

@Component({
  selector: 'app-conversa-cabecalho',
  standalone: true,
  imports: [MatTooltip, CelularMaskPipe],
  animations: [animacaoRevelar, animacaoExpandir],
  templateUrl: './conversa-cabecalho.component.html',
  styleUrl: './conversa-cabecalho.component.css'
})
export class ConversaCabecalhoComponent implements OnInit {
  protected readonly CanalOrigemContatoEnum = CanalOrigemContatoEnum;

  @Input({ required: true }) contato!: Contato;
  @Input() carregando = false;
  @Input() qtdLembretes = 0;
  @Input() mostrarVoltar = false;
  readonly sessaoAberta = input(false);
  readonly dataFechamento = input<Date | null>(null);

  @Output() voltar = new EventEmitter<void>();
  @Output() abrirPerfil = new EventEmitter<void>();
  @Output() alternarFavorito = new EventEmitter<void>();
  @Output() abrirLembretes = new EventEmitter<void>();
  @Output() editarContato = new EventEmitter<void>();

  private readonly agora = signal(Date.now());

  private readonly mostrarNumero = signal(MethodsUtils.lerStorage(CHAVE_MOSTRAR_NUMERO) === '1');
  private readonly numeroOculto = signal(true);
  private temporizadorCopiado: ReturnType<typeof setTimeout> | null = null;
  numeroCopiado = signal(false);

  podeVerNumero(): boolean {
    return !this.numeroOculto() && !!this.contato?.numero && this.contato.canalOrigem !== CanalOrigemContatoEnum.INSTAGRAM;
  }

  numeroVisivel(): boolean {
    return this.mostrarNumero() && this.podeVerNumero();
  }

  alternarNumero(event: Event): void {
    event.stopPropagation();
    const mostrar = !this.mostrarNumero();
    this.mostrarNumero.set(mostrar);
    MethodsUtils.salvarStorage(CHAVE_MOSTRAR_NUMERO, mostrar ? '1' : '0');
  }

  copiarNumero(event: Event): void {
    event.stopPropagation();
    const numero = String(this.contato?.numero ?? '').replace(/\D/g, '');
    if (!numero) return;
    navigator.clipboard?.writeText(numero).then(() => {
      this.numeroCopiado.set(true);
      if (this.temporizadorCopiado) clearTimeout(this.temporizadorCopiado);
      this.temporizadorCopiado = setTimeout(() => this.numeroCopiado.set(false), 1500);
    }).catch(() => undefined);
  }

  readonly tempoJanela = computed(() => {
    const fechamento = this.dataFechamento();
    if (!this.sessaoAberta() || !fechamento) return '';
    const restanteMs = new Date(fechamento).getTime() - this.agora();
    if (restanteMs <= 0) return '';
    const horas = Math.floor(restanteMs / 3_600_000);
    const minutos = Math.floor((restanteMs % 3_600_000) / 60_000);
    return `${horas}h ${minutos}min`;
  });

  private readonly destroyRef = inject(DestroyRef);
  private readonly restricaoService = inject(RestricaoService);

  ngOnInit(): void {
    interval(30_000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.agora.set(Date.now()));
    this.restricaoService.buscarOcultarNumeroCelular().pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(ocultar => this.numeroOculto.set(ocultar));
  }

  get fotoContato(): string {
    return MethodsUtils.fotoPerfilContato(this.contato.fotoPerfilUrl, this.contato.statusContato);
  }

  get possuiFotoPerfil(): boolean {
    return MethodsUtils.fotoPerfilDisponivel(this.contato.fotoPerfilUrl);
  }

  marcarFotoIndisponivel(): void {
    MethodsUtils.marcarFotoPerfilIndisponivel(this.contato.fotoPerfilUrl);
  }

  get tooltipFoto(): string {
    return MethodsUtils.informacoesPorStatusContato(this.contato.statusContato, false);
  }

  get logoCanalOrigem(): string | null {
    switch (this.contato.canalOrigem) {
      case CanalOrigemContatoEnum.INSTAGRAM:
        return './assets/logos/Instagram_logo_2022.svg.webp';
      case CanalOrigemContatoEnum.WHATSAPP:
        return './assets/logos/wpp-logo.png';
      default:
        return null;
    }
  }
}
